import { Canvas } from "@react-three/fiber";
import { useState } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useObserverStore } from "../../stores/observerStore";
import { AgentMarkers } from "../AgentOverlay/AgentMarkers";
import { TradePulses } from "../AgentOverlay/TradePulses";
import { TravelArcs } from "../AgentOverlay/TravelArcs";
import { GalaxyView } from "./GalaxyView";
import { SystemView } from "./SystemView";

function ViewOverlays({ mode }: { mode: "galaxy" | "system" }): React.JSX.Element {
	const galaxyScale = useObserverStore((s) => s.galaxyView.scale);
	const systemScale = useObserverStore((s) => s.systemView.scale);
	const systemCount = useObserverStore((s) => s.systems.length);
	const focusedSystem = useObserverStore((s) => s.focusedSystem);

	const scale = mode === "galaxy" ? galaxyScale : systemScale;
	const planetCount = focusedSystem?.planets.length ?? 0;

	return (
		<>
			<div
				style={{
					position: "absolute",
					bottom: "12px",
					left: "12px",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					padding: "0.4rem 0.6rem",
					background: "rgba(10, 10, 15, 0.7)",
					border: "1px solid rgba(26, 26, 46, 0.6)",
					borderRadius: "6px",
					zIndex: 15,
				}}
			>
				<span
					style={{
						fontSize: "0.6rem",
						color: "#565680",
						textTransform: "uppercase",
						letterSpacing: "0.1em",
					}}
				>
					Zoom
				</span>
				<span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#2dd4a8" }}>
					{(scale * 100).toFixed(0)}%
				</span>
			</div>
			<div
				style={{
					position: "absolute",
					bottom: "12px",
					left: "100px",
					padding: "0.4rem 0.6rem",
					background: "rgba(10, 10, 15, 0.7)",
					border: "1px solid rgba(26, 26, 46, 0.6)",
					borderRadius: "6px",
					fontSize: "0.7rem",
					color: "#c8c8d0",
					zIndex: 15,
				}}
			>
				{mode === "galaxy" ? (
					<span>{systemCount} systems</span>
				) : (
					<span>{planetCount} planets</span>
				)}
			</div>
		</>
	);
}

export function SpaceCanvas(): React.JSX.Element {
	const viewMode = useObserverStore((s) => s.viewMode);
	const [transitioning, setTransitioning] = useState(false);
	const [displayMode, setDisplayMode] = useState(viewMode);
	const { isMobile } = useDeviceCapabilities();

	// Handle view transitions with simple opacity overlay
	const prevModeRef = useObserverStore((s) => s.viewMode);
	if (prevModeRef !== displayMode && !transitioning) {
		setTransitioning(true);
		setTimeout(() => {
			setDisplayMode(prevModeRef);
			setTimeout(() => setTransitioning(false), 200);
		}, 150);
	}

	return (
		<div
			style={{
				position: "absolute",
				inset: 0,
				overflow: "hidden",
				cursor: "grab",
			}}
		>
			<Canvas
				orthographic
				dpr={[1, 1.5]}
				gl={{ alpha: false }}
				camera={{ position: [0, 0, 100], zoom: 1, near: 0.1, far: 1000 }}
				scene={{ background: new THREE.Color("#060d1e") }}
				style={{ position: "absolute", inset: 0 }}
			>
				{displayMode === "galaxy" ? <GalaxyView /> : <SystemView />}
				{/* Agent overlay — rendered inside Canvas for Three.js integration */}
				<AgentMarkers />
				<TravelArcs />
				<TradePulses />
			</Canvas>

			{/* HTML overlays */}
			{displayMode === "galaxy" && <div className="galaxy-tooltip" style={{ display: "none" }} />}

			{/* Mobile crosshair */}
			{isMobile && displayMode === "galaxy" && (
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						pointerEvents: "none",
						zIndex: 5,
					}}
				>
					<svg width="24" height="24" viewBox="0 0 24 24" role="img" aria-label="Crosshair">
						<circle
							cx="12"
							cy="12"
							r="8"
							fill="none"
							stroke="rgba(255,255,255,0.3)"
							strokeWidth="1"
						/>
						<line x1="12" y1="2" x2="12" y2="6" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
						<line x1="12" y1="18" x2="12" y2="22" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
						<line x1="2" y1="12" x2="6" y2="12" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
						<line x1="18" y1="12" x2="22" y2="12" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
					</svg>
				</div>
			)}

			<ViewOverlays mode={displayMode} />

			{/* Transition overlay (CSS-based, no framer-motion) */}
			{transitioning && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						background: "#060d1e",
						opacity: 0.7,
						pointerEvents: "none",
						zIndex: 10,
						transition: "opacity 0.15s ease",
					}}
				/>
			)}
		</div>
	);
}
